<template>
  <md-dialog :md-active.sync="active" class="biotime-area-modal" @md-closed="onClosed">
    <md-dialog-title>
      {{ isEdit ? 'Edit Biotime Area' : 'Add Biotime Area' }}
    </md-dialog-title>

    <md-dialog-content>
      <form @submit.prevent="submit">
        <div class="modal-content">
          <!-- Area Code -->
          <div class="form-field">
            <label class="field-label">Area Code *</label>
            <md-field :class="{ 'md-invalid': errors.area_code }">
              <md-input
                  v-model="form.area_code"
                  required
                  placeholder="e.g. AREA-001"
                  @input="clearError('area_code')"
              />
            </md-field>
            <span class="md-error" v-if="errors.area_code">{{ errors.area_code }}</span>
          </div>

          <!-- Area Name -->
          <div class="form-field">
            <label class="field-label">Area Name *</label>
            <md-field :class="{ 'md-invalid': errors.area_name }">
              <md-input
                  v-model="form.area_name"
                  required
                  placeholder="e.g. Main Entrance"
                  @input="clearError('area_name')"
              />
            </md-field>
            <span class="md-error" v-if="errors.area_name">{{ errors.area_name }}</span>
          </div>

          <!-- Parent Area -->
          <div class="form-field">
            <label class="field-label">Parent Area</label>
            <md-field>
              <md-input
                  v-model="form.parent_area"
                  placeholder="e.g. Building A (optional)"
              />
            </md-field>
          </div>

          <!-- Station -->
          <div class="form-field">
            <label class="field-label">Station</label>
            <md-field>
              <md-select v-model="form.station_id" placeholder="Unassigned">
                <md-option :value="null">Unassigned</md-option>
                <md-option
                    v-for="station in stations"
                    :key="station.id"
                    :value="station.id"
                >
                  {{ station.name }}
                </md-option>
              </md-select>
            </md-field>
            <small class="field-hint">Leave empty to keep this area unassigned.</small>
          </div>

          <!-- General error -->
          <div v-if="generalError" class="error-banner">
            <md-icon>error</md-icon>
            <span>{{ generalError }}</span>
          </div>
        </div>
      </form>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button class="md-primary" @click="submit" :disabled="isSubmitting">
        {{ isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Add Area') }}
      </md-button>
      <md-button class="md-danger" @click="close">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
export default {
  name: 'BiotimeAreaFormModal',
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null },
    stations: { type: Array, default: function () { return []; } }
  },
  data() {
    return {
      isSubmitting: false,
      generalError: null,
      errors: {},
      form: {
        area_code: '',
        area_name: '',
        parent_area: '',
        station_id: null
      }
    };
  },
  computed: {
    active: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    },
    isEdit() {
      return !!(this.item && this.item.id);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) this.initializeForm();
      }
    }
  },
  methods: {
    initializeForm() {
      this.errors = {};
      this.generalError = null;

      if (this.isEdit && this.item) {
        this.form = {
          area_code: this.item.area_code || '',
          area_name: this.item.area_name || '',
          parent_area: this.item.parent_area || '',
          station_id: this.item.station_id || null
        };
      } else {
        this.form = {
          area_code: '',
          area_name: '',
          parent_area: '',
          station_id: null
        };
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    validate() {
      this.errors = {};
      if (!this.form.area_code || !this.form.area_code.trim()) {
        this.errors.area_code = 'Area code is required';
      }
      if (!this.form.area_name || !this.form.area_name.trim()) {
        this.errors.area_name = 'Area name is required';
      }
      return Object.keys(this.errors).length === 0;
    },

    async submit() {
      if (!this.validate()) return;

      this.isSubmitting = true;
      this.generalError = null;

      try {
        var payload = {
          type: 'biotimeAreas',
          area_code: this.form.area_code.trim(),
          area_name: this.form.area_name.trim(),
          parent_area: this.form.parent_area ? this.form.parent_area.trim() : null,
          station_id: this.form.station_id || null
        };

        if (this.isEdit) {
          payload.id = this.item.id;
          await this.$store.dispatch('biotimeAreas/update', payload);
        } else {
          await this.$store.dispatch('biotimeAreas/add', payload);
        }

        this.$emit('success', { isEdit: this.isEdit });
        this.$emit('input', false);
      } catch (error) {
        console.error('Error saving area:', error);

        if (error.response && error.response.data && error.response.data.errors) {
          var apiErrors = error.response.data.errors;
          var self = this;
          if (Array.isArray(apiErrors)) {
            apiErrors.forEach(function (err) {
              var field = 'general';
              if (err.source && err.source.pointer) {
                field = err.source.pointer.split('/').pop();
              }
              if (field === 'general') {
                self.generalError = err.detail;
              } else {
                self.$set(self.errors, field, err.detail);
              }
            });
          }
        } else {
          this.generalError = 'Failed to save area. Please try again.';
        }

        this.$emit('error', error);
      } finally {
        this.isSubmitting = false;
      }
    },

    close() {
      this.active = false;
    },

    onClosed() {
      this.errors = {};
      this.generalError = null;
      this.isSubmitting = false;
      this.form = {
        area_code: '',
        area_name: '',
        parent_area: '',
        station_id: null
      };
      this.$emit('closed');
    }
  }
};
</script>

<style lang="scss" scoped>
.biotime-area-modal {
  ::v-deep .md-dialog {
    max-width: 520px;
    width: 90%;
  }

  ::v-deep .md-dialog-title {
    padding: 20px 24px 0;
    font-size: 18px;
    font-weight: 600;
  }

  ::v-deep .md-dialog-content {
    padding: 8px 24px 16px;
  }

  ::v-deep .md-dialog-actions {
    padding: 8px 24px 20px;
  }
}

.modal-content {
  min-width: 400px;
  padding-top: 8px;
}

.form-field {
  margin-bottom: 16px;

  .field-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #555;
    margin-bottom: 6px;
  }

  .field-hint {
    display: block;
    font-size: 11px;
    color: #999;
    margin-top: 4px;
  }
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #ffebee;
  border-radius: 6px;
  color: #c62828;
  font-size: 13px;

  .md-icon {
    color: #c62828;
    font-size: 18px;
  }
}

.md-error {
  font-size: 12px;
  color: #f44336;
  margin-top: 4px;
  display: block;
}

@media (max-width: 768px) {
  .modal-content {
    min-width: auto;
  }
}
</style>